import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { LineChart } from '@mui/x-charts/LineChart'
import { formatDate } from 'date-fns/format'
import { LineSeriesType } from '@mui/x-charts'
import { getMuiDataGridLocaleText, useAppLocale } from '../../../i18n/localeUtils'
import { useTranslation } from 'react-i18next'
import RadioGroupLabeled from '../RadioGroupLabeled'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridColDef } from '@mui/x-data-grid-pro'
import Grid from '@mui/material/Grid'
import { Box, Typography } from '@mui/material'
import { IssueEntity } from 'types/IssueEntity'
import { formatSeries, getDataForChart, groupByTimeUnit } from './utils'
import { formatDateYYYYMMDD, formatPercentage } from '../../../packages/utils/numberFormatters'
import { useLogUserEvent } from '../../../hooks/useLogUserEvent'
import RoutedDropdownSelect from '../../components/RoutedDropdownSelect'
import { useQuery } from '../../../hooks/useQuery'
import { startOfQuarter } from 'date-fns/startOfQuarter'
import { startOfYear } from 'date-fns'
import { exportDataGridProps } from '../issue-market/utils'
import { USER_EVENT_TYPES } from '@finance-lab/utils/types'

const dropdownItemKeys = [
    { id: 'commitment', labelKey: 'charts.trendGraphs.commitment' },
    { id: 'subscription', labelKey: 'charts.trendGraphs.subscription' },
    { id: 'guarantor-fee-issue', labelKey: 'charts.trendGraphs.guarantorFee' },
    { id: 'discount', labelKey: 'charts.trendGraphs.discount' },
] as const

type TrendGraphType = (typeof dropdownItemKeys)[number]['id']

const TrendChart = ({ isLoading, issues }: { isLoading: boolean; issues: IssueEntity[] }) => {
    const [selectedTimeUnit, setSelectedTimeUnit] = useState<'quarter' | 'year'>('quarter')
    const {
        t,
        i18n: { language: locale },
    } = useTranslation()
    const { dateFnsLocale } = useAppLocale()
    const query = useQuery()
    const navigate = useNavigate()

    const dropdownItems = useMemo(() => dropdownItemKeys.map(({ id, labelKey }) => ({ id, label: t(labelKey) })), [t])

    const handleChangeSerie = ([id]: string[]) => {
        query.set('serie', id)
        void navigate('?' + query.toString())
    }
    const defaultGraph = 'commitment'
    const chartRoutingParam = 'graph'
    const selectedChart: TrendGraphType =
        dropdownItemKeys.find((i) => i.id === query.get(chartRoutingParam))?.id || defaultGraph
    const {
        excludeNullValues,
        seriesData: dataset,
        series: rawSeries,
    } = React.useMemo(() => getDataForChart(selectedChart, issues), [selectedChart, issues])

    const series = useMemo(
        () => rawSeries.map(({ labelKey, ...rest }) => ({ ...rest, label: t(labelKey) })),
        [rawSeries, t],
    )

    const defaultSerie = series[0].id
    const serieRoutingParam = 'serie'
    const selectedSerie = series.find((s) => s.id === query.get(serieRoutingParam))?.id || defaultSerie

    useLogUserEvent(USER_EVENT_TYPES.STATS_TREND_GRAPHS_SELECTION, {
        selectedChart,
        selectedSerie,
        selectedTimeUnit,
    })

    const groupedDataset = groupByTimeUnit(
        dataset,
        selectedTimeUnit,
        series.map((serie) => serie.id),
        excludeNullValues,
        dateFnsLocale,
    )
    const formattedDataset = formatSeries(groupedDataset, selectedTimeUnit, dateFnsLocale)

    const columns: GridColDef[] = [
        {
            field: 'time',
            headerAlign: 'left',
            headerName: '',
        },
        {
            field: `${selectedSerie}Count`,
            headerAlign: 'left',
            headerName: t('site.table.count'),
        },
        {
            field: `${selectedSerie}Min`,
            headerAlign: 'left',
            headerName: t('site.table.min'),
            type: 'number',
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
        {
            field: `${selectedSerie}Max`,
            headerAlign: 'left',
            headerName: t('site.table.max'),
            type: 'number',
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
        {
            field: `${selectedSerie}Average`,
            headerAlign: 'left',
            headerName: t('site.table.average'),
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
    ]

    const formattedSeries: Omit<LineSeriesType, 'type'>[] = series.map((serie) => ({
        ...serie,
        showMark: true,
        curve: 'catmullRom',
        highlightScope: {
            highlight: 'series',
            fade: 'global',
        },
        valueFormatter: (value: number | null) => `${value == null ? '-' : formatPercentage(value, { locale })}`,
    }))

    return (
        <Grid container spacing={10}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ marginBottom: 4 }}>
                    <RoutedDropdownSelect
                        defaultItem={defaultGraph}
                        items={dropdownItems}
                        label={t('pages.statistics.trends.selectChart')}
                        routingParam={chartRoutingParam}
                    />
                </Box>
                <RadioGroupLabeled<'quarter' | 'year'>
                    label={t('pages.statistics.trends.timeUnit')}
                    onChange={setSelectedTimeUnit}
                    options={{
                        quarter: t('charts.timeUnit.quarter'),
                        year: t('charts.timeUnit.year'),
                    }}
                    value={selectedTimeUnit}
                />
                <LineChart
                    dataset={formattedDataset}
                    height={500}
                    loading={isLoading}
                    margin={{
                        top: 100,
                        right: 30,
                    }}
                    xAxis={[
                        {
                            dataKey: 'date',
                            tickInterval: (d) =>
                                selectedTimeUnit === 'quarter'
                                    ? formatDateYYYYMMDD(d, { disableNonBreakingDash: true }) ===
                                      formatDateYYYYMMDD(startOfQuarter(d), { disableNonBreakingDash: true })
                                    : formatDateYYYYMMDD(d, { disableNonBreakingDash: true }) ===
                                      formatDateYYYYMMDD(startOfYear(d), { disableNonBreakingDash: true }),

                            scaleType: 'point',
                            valueFormatter: (value) =>
                                formatDate(value, selectedTimeUnit === 'quarter' ? 'yyyy QQQ' : 'yyyy', {
                                    locale: dateFnsLocale,
                                }),
                        },
                    ]}
                    onMarkClick={(_event, { seriesId }) => handleChangeSerie([seriesId])}
                    onLineClick={(_event, { seriesId }) => handleChangeSerie([seriesId])}
                    series={formattedSeries}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                    variant='h6'
                    gutterBottom
                    sx={{
                        marginBottom: 4,
                    }}
                >
                    <RoutedDropdownSelect
                        defaultItem={defaultSerie}
                        items={series}
                        label={t('pages.statistics.trends.selectSeries')}
                        routingParam={serieRoutingParam}
                    />
                </Typography>
                <DataGridPro
                    {...exportDataGridProps}
                    columns={columns}
                    loading={isLoading}
                    localeText={getMuiDataGridLocaleText(locale)}
                    rows={formattedDataset}
                    hideFooter
                    sx={{ height: 'auto' }}
                />
            </Grid>
        </Grid>
    )
}

export default TrendChart
